import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DraggableData } from 'react-draggable';
import linq from 'linq';

import { isMapCard, MapCard, CardDb, updateCard } from '../redux/cardDbSlice';
import { ArrowDb } from '../redux/arrowDbSlice';
import { deepCopy, CoordinateType, Hash } from '../general';
import { getCardComponent, getCardSize } from '../card_components';
import CardPad from './CardPad';
import Arrow from './Arrow';
import { CardMenuAction, ArrowMenuAction, OperationMode, ShowMenuType } from './BookEditor';

type DisplayPositionType = { xLeft: number; xRight: number; yTop: number; yBottom: number; };

type CardSetProps = {
  cardList: Hash<MapCard>;
  groupList: Hash<string[]>;
  id: string;
  setCard: (id: string, value: CardDb) => void;
  getDisplayPosition: (id: string, data?: DraggableData) => DisplayPositionType;
  cardMenuClicked: CardMenuAction;
  showMenu: ShowMenuType;
  setShowMenu: (value: ShowMenuType) => void;
  closeArrowMenu: () => void;
  updateGroup: (id: string, card: MapCard, data: DraggableData) => void;
  mode?: 'move' | 'connect';
  connectId?: string | undefined;
  handleChangeConnectId: (id: string) => void;
  scaleCoefficient?: number;
  cardResizeAction: { show: boolean; id?: string; };
  setDraggingId?: (id?: string) => void;
  getRaised: (id: string) => boolean;
  foregroundOverlappedId: string | undefined;
  updateOverlappedId: (data: DraggableData) => void;
  getInternalPosition: (id: string, data: DraggableData) => CoordinateType;
}
const CardSet: React.FC<CardSetProps> = (props) => {
  const {cardList, groupList, id, setCard, getDisplayPosition, cardMenuClicked, showMenu, setShowMenu, closeArrowMenu, updateGroup, mode, connectId, handleChangeConnectId, scaleCoefficient, cardResizeAction, setDraggingId, getRaised, foregroundOverlappedId, updateOverlappedId, getInternalPosition} = props;
  const card = cardList[id];
  const group = groupList[id];

  if (!card) return <></>;
  return (
    <>
      <CardPad key={id} id={id}
        card={{ ...card, x: getDisplayPosition(id).xLeft, y: getDisplayPosition(id).yTop }}
        setCard={value => setCard(id, value)}
        cardMenuClicked={cardMenuClicked}
        showMenu={showMenu} setShowMenu={setShowMenu}
        closeArrowMenu={closeArrowMenu}
        updateGroup={(card, data) => updateGroup(id, card, data)}
        mode={mode} connectId={connectId}
        handleChangeConnectId={handleChangeConnectId}
        scaleCoefficient={scaleCoefficient}
        resizing={cardResizeAction.show && id === cardResizeAction.id}
        setDraggingId={setDraggingId}
        raised={getRaised(id)}
        overlapped={id === foregroundOverlappedId}
        updateOverlappedId={updateOverlappedId}
        getInternalPosition={getInternalPosition}
      >
        { getCardComponent(card.kind, id) }
      </CardPad>
      { group && group.map(childId => {
        return (
          <CardSet
            cardList={cardList} groupList={groupList}
            id={childId}
            setCard={(id, value) => setCard(id, value)}
            getDisplayPosition={getDisplayPosition}
            cardMenuClicked={cardMenuClicked}
            showMenu={showMenu} setShowMenu={setShowMenu}
            closeArrowMenu={closeArrowMenu}
            updateGroup={(id, card, data) => updateGroup(id, card, data)}
            mode={mode} connectId={connectId}
            handleChangeConnectId={handleChangeConnectId}
            scaleCoefficient={scaleCoefficient}
            cardResizeAction={cardResizeAction}
            setDraggingId={setDraggingId}
            getRaised={getRaised}
            foregroundOverlappedId={foregroundOverlappedId}
            updateOverlappedId={updateOverlappedId}
            getInternalPosition={getInternalPosition}
          />
        );
      }) }
    </>
  );
}
type MapSheetProps = {
  ids: string[];
  cards: MapCard[];
  arrowIds: string[];
  arrows: ArrowDb[];
  showMenu: ShowMenuType;
  setShowMenu: (value: ShowMenuType) => void;
  toggleArrowMenuData: (id?: string, arrow?: ArrowDb, ev?: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  selectedId: string | undefined;
  mode: OperationMode;
  connectId: string | undefined;
  handleChangeConnectId: (id: string) => void;
  cardMenuClicked: CardMenuAction;
  arrowMenuClicked: ArrowMenuAction;
  scaleCoefficient: number;
  cardResizeAction: { show: boolean; id?: string; };
  putDownForwardCard: (isGroup: boolean, groupParent: string | undefined, z: number) => void;
  getForegroundZ: (isGroup: boolean, groupParent: string | undefined) => number;
}
const MapSheet: React.FC<MapSheetProps> = ({ids, cards, arrowIds, arrows, showMenu, setShowMenu, toggleArrowMenuData, selectedId, mode, connectId, handleChangeConnectId, cardMenuClicked, arrowMenuClicked, scaleCoefficient, cardResizeAction, putDownForwardCard, getForegroundZ}) => {
  const dispatch = useDispatch();
  const mapSheetId = 'map-sheet-work-area';
  const [cardList, setCardList] = useState<Hash<MapCard>>({});
  const [groupList, setGroupList] = useState<Hash<string[]>>({});
  const [draggingId, setDraggingId] = useState<string>();
  const [foregroundOverlappedId, setForegroundOverlappedId] = useState<string>();

  useEffect(() => {
    const newCardList: Hash<MapCard> = {};
    const newGroupList: Hash<string[]> = {};
    ids.forEach((id, ix) => {
      const card = cards[ix];
      if (card.isGroup) newGroupList[id] = [];
    })
    ids.forEach((id, ix) => {
      const card = cards[ix];
      newCardList[id] = card;
      const parent = card.groupParent;
      if (parent && newGroupList[parent]) newGroupList[parent].push(id);
    })
    setCardList(newCardList);
    setGroupList(newGroupList);
  }, [cards])

  const getDisplayPosition = (id: string, data?: DraggableData): DisplayPositionType => {
    const card = cardList[id];
    if (!card) return { xLeft: 0, xRight: 0, yTop: 0, yBottom: 0 };
    let xLeft = data ? data.x : card.x;
    let yTop = data ? data.y : card.y;
    if (!data && card.groupParent) {
      xLeft += getDisplayPosition(card.groupParent).xLeft;
      yTop += getDisplayPosition(card.groupParent).yTop;
    }
    const xRight = xLeft + getCardSize(card).width;
    const yBottom = yTop + getCardSize(card).height;
    return { xLeft, xRight, yTop, yBottom };
  }
  const isOverlapped = (targetId: string, id: string, data: DraggableData) => {
    const targetPosition = getDisplayPosition(targetId);
    const cardPosition = getDisplayPosition(id, data);
    if (targetPosition.xLeft < cardPosition.xLeft && cardPosition.xRight < targetPosition.xRight &&
        targetPosition.yTop < cardPosition.yTop && cardPosition.yBottom < targetPosition.yBottom
    ) return true;
    else return false;
  }
  const getForegroundOverlappedId = (id: string, data: DraggableData): string | undefined => {
    const searchElFromId = (elements: Element[], target: HTMLElement | null): Element | undefined => {
      if (!target) return undefined;
      if (Array.from(target.children) === elements) return undefined;
      else if (elements.includes(target)) return target;
      else if (target.parentElement) return searchElFromId(elements, target.parentElement);
      else return undefined;
    }
    const groupIds = Object.keys(groupList);
    const targetIds = linq.from(groupIds)
      .where(targetId => isOverlapped(targetId, id, data))
      .toArray();
    if (targetIds.length === 0) return;
    else {
      const sheet = document.getElementById(mapSheetId);
      const children = sheet?.children;
      if (!children) return;
      const childrenArray = Array.from(children);
      const res = linq.from(targetIds).orderByDescending(targetId => {
        const targetEl = searchElFromId(childrenArray, document.getElementById(targetId));
        if (targetEl) return childrenArray.indexOf(targetEl);
        else return 0;
      }).toArray();
      return res[0];
    }
  }
  const updateGroup = (id: string, card: MapCard, data: DraggableData) => {
    let x = data.x;
    let y = data.y;
    let z = card.z;
    const nextParent = getForegroundOverlappedId(id, data);
    if (nextParent) {
      x = getDisplayPosition(id, data).xLeft - getDisplayPosition(nextParent).xLeft;
      y = getDisplayPosition(id, data).yTop - getDisplayPosition(nextParent).yTop;
    } else {
      x = getDisplayPosition(id, data).xLeft;
      y = getDisplayPosition(id, data).yTop;
    }
    if (nextParent !== card.groupParent) {
      z = getForegroundZ(card.isGroup, nextParent);
      putDownForwardCard(card.isGroup, card.groupParent, card.z);
    }

    setForegroundOverlappedId(undefined);
    dispatch(updateCard({ id, x, y, z, groupParent: nextParent }));
  }
  const getInternalPosition = (id: string, data: DraggableData): CoordinateType => {
    const card = cardList[id];
    if (!card) return { x: 0, y: 0 };
    let x = data.x;
    let y = data.y;
    if (card.groupParent) {
      x -= getDisplayPosition(card.groupParent).xLeft;
      y -= getDisplayPosition(card.groupParent).yTop;
    }
    return { x, y };
  }
  const getCardDisplayData = (id: string): MapCard | undefined => {
    const card = cardList[id];
    if (!card) return;
    return { ...card, x: getDisplayPosition(id).xLeft, y: getDisplayPosition(id).yTop };
  }
  const getRaised = (id: string): boolean => {
    if (draggingId === id) return true;
    const card = cardList[id];
    if (card && card.groupParent) return getRaised(card.groupParent);
    else return false;
  }
  const updateOverlappedId = (data: DraggableData) => {
    if (!draggingId) return;
    setForegroundOverlappedId(getForegroundOverlappedId(draggingId, data));
  }

  return (
    <div id={mapSheetId}>
      { ids.map(id => {
        const card = cardList[id];
        if (!card || card.groupParent) return;
        return (
          <CardSet
            cardList={cardList} groupList={groupList}
            id={id}
            setCard={(id, value) => {
              if (!isMapCard(value)) return;
              const newCardList = deepCopy(cardList);
              newCardList[id] = value;
              setCardList(newCardList);
            }}
            getDisplayPosition={getDisplayPosition}
            cardMenuClicked={cardMenuClicked}
            showMenu={showMenu} setShowMenu={setShowMenu}
            closeArrowMenu={() => toggleArrowMenuData()}
            updateGroup={(id, card, data) => updateGroup(id, card, data)}
            mode={mode} connectId={connectId}
            handleChangeConnectId={handleChangeConnectId}
            scaleCoefficient={scaleCoefficient}
            cardResizeAction={cardResizeAction}
            setDraggingId={setDraggingId}
            getRaised={getRaised}
            foregroundOverlappedId={foregroundOverlappedId}
            updateOverlappedId={updateOverlappedId}
            getInternalPosition={getInternalPosition}
          />
        );
      }) }
      { arrowIds.map((id, ix) => {
        const arrow = arrows[ix];
        let sibLength = 1;
        arrowIds.forEach((targetId, targetIx) => {
          if (targetId === id) return;
          const target = arrows[targetIx];
          if ((target.fromId === arrow.fromId && target.toId === arrow.toId) || (target.fromId === arrow.toId && target.toId === arrow.fromId)) sibLength++;
        })
        return (
          <Arrow key={id} id={id} data={arrow}
            tailCard={getCardDisplayData(arrow.fromId)}
            headCard={getCardDisplayData(arrow.toId)}
            sibLength={sibLength}
            selected={id === selectedId}
            arrowMenuClicked={arrowMenuClicked}
            onContextMenu={(ev) => toggleArrowMenuData(id, arrow, ev)}
          />
        );
      }) }
    </div>
  );
}

export default MapSheet;
